import { useEffect, useState } from "react";
import { Link, useSearchParams, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Info,
  Search,
  ArrowLeft,
  Loader2,
  SearchX,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { agentApi, AgentCourse } from "@/services/agentApi";
import { courseApplicationUrl } from "@/utils/courseSelection";
import "./courses.css";

const fallback = (value?: string | null) => value?.trim() || "Not provided";
function CourseFields({
  course,
  details = false,
}: {
  course: AgentCourse;
  details?: boolean;
}) {
  return (
    <dl className="course-metadata">
      {details && (
        <div>
          <dt>Programme</dt>
          <dd>{course.programmeName}</dd>
        </div>
      )}
      <div>
        <dt>Institution</dt>
        <dd>{course.institutionName}</dd>
      </div>
      <div>
        <dt>Study level</dt>
        <dd>{fallback(course.level)}</dd>
      </div>
      <div>
        <dt>Location</dt>
        <dd>
          {fallback([course.city, course.country].filter(Boolean).join(", "))}
        </dd>
      </div>
      <div className="course-intakes">
        <dt>Intake</dt>
        <dd>
          {fallback(course.startDate || course.intakeDates)
            .split(/\r?\n/)
            .map((intake) => intake.trim())
            .filter(Boolean)
            .map((intake, index) => <span key={index}>{intake}</span>)}
        </dd>
      </div>
      {details && (
        <div>
          <dt>Application deadline</dt>
          <dd>{fallback(course.applicationDeadline)}</dd>
        </div>
      )}
      <div>
        <dt>Availability</dt>
        <dd>
          <span
            className={`course-availability ${course.canApply ? "listed" : "restricted"}`}
          >
            {course.canApply ? "Listed" : course.availability}
          </span>
        </dd>
      </div>
    </dl>
  );
}
function ApplyAction({ course }: { course: AgentCourse }) {
  const { user } = useAuth();
  const canReviewPartnership = !user?.agentProfile?.parentAgentId || user.agentProfile.canAccessPartnerships === true;
  return (
    <div className="course-actions">
      <Link className="btn btn-outline-primary btn-sm" to={`/eligibility?offeringId=${course.offeringId}`}>Check eligibility</Link>
      {course.canApply ? (
        <Link
          className="btn btn-primary btn-sm"
          to={courseApplicationUrl({
            universityId: course.universityId,
            offeringId: course.offeringId,
          })}
        >
          Apply
        </Link>
      ) : (
        <>
          <button className="btn course-apply-disabled btn-sm" disabled>
            Apply
          </button>
          <span className="course-disabled-reason">
            {course.availability}
          </span>
          {canReviewPartnership && <Link className="small" to={`/partnerships?institutionId=${course.universityId}`}>Review institution partnership</Link>}
        </>
      )}
    </div>
  );
}
function QueryError({ retry }: { retry: () => void }) {
  return (
    <div className="course-card p-4" role="alert">
      <AlertCircle size={28} className="text-danger mb-3" aria-hidden="true" />
      <h2 className="h6">Unable to load courses</h2>
      <p className="text-muted">
        Please try again. Your filters have been kept.
      </p>
      <button className="btn btn-outline-primary btn-sm" onClick={retry}>
        Retry
      </button>
    </div>
  );
}
export function CourseSearchPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const search = params.get("search") || "";
  const [input, setInput] = useState(search);
  const universityId = params.get("universityId") || "";
  const level = params.get("level") || "";
  const invalidFilter =
    params.getAll("universityId").length > 1 ||
    params.getAll("level").length > 1 ||
    params.getAll("search").length > 1 ||
    params.getAll("page").length > 1 ||
    (universityId !== "" &&
      (!/^[1-9]\d*$/.test(universityId) ||
        !Number.isSafeInteger(Number(universityId))));
  const rawPage = params.get("page") || "1";
  const page =
    /^[1-9]\d*$/.test(rawPage) && Number(rawPage) <= 1000000
      ? Number(rawPage)
      : 1;
  const validFilters =
    !invalidFilter && /^[1-9]\d*$/.test(rawPage) && Number(rawPage) <= 1000000;
  useEffect(() => {
    setInput(search);
  }, [search]);
  useEffect(() => {
    if (input === search) return;
    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(params);
      input.trim() ? next.set("search", input.trim()) : next.delete("search");
      next.delete("page");
      setParams(next);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [input, search, params, setParams]);
  const options = useQuery(
    ["course-options", user?.id],
    ({ signal }) => agentApi.getCourseOptions(signal),
    { enabled: !!user?.id },
  );
  const query = useQuery(
    ["courses", user?.id, search, universityId, level, page],
    ({ signal }) =>
      agentApi.getCourses(
        {
          search: search || undefined,
          universityId: universityId ? Number(universityId) : undefined,
          level: level || undefined,
          page,
          limit: 12,
        },
        signal,
      ),
    {
      enabled: !!user?.id && validFilters,
      keepPreviousData: false,
    },
  );
  function filter(key: string, value: string) {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    next.delete("page");
    setParams(next);
  }
  return (
    <div className="course-search container-fluid py-4">
      <header className="mb-4">
        <h1>Course Search</h1>
        <p>
          Browse courses from our exclusive university catalogue. Apply through
          your approved institution partnerships.
        </p>
      </header>
      <div className="course-layout">
        <aside>
          <div className="course-card course-filters">
            <label htmlFor="course-search">Search</label>
            <div className="course-search-input">
              <input
                className="form-control form-control-sm"
                id="course-search"
                value={input}
                maxLength={200}
                placeholder="Search courses or institutions"
                onChange={(event) => setInput(event.target.value)}
              />
              <Search size={16} aria-hidden="true" />
            </div>
            <label htmlFor="course-institution">Institution</label>
            <select
              className="form-select form-select-sm"
              id="course-institution"
              value={universityId}
              onChange={(event) => filter("universityId", event.target.value)}
            >
              <option value="">All Institutions</option>
              {options.data?.institutions.map((institution) => (
                <option key={institution.id} value={institution.id}>
                  {institution.name}
                </option>
              ))}
            </select>
            <label htmlFor="course-level">Study Level</label>
            <select
              className="form-select form-select-sm"
              id="course-level"
              value={level}
              onChange={(event) => filter("level", event.target.value)}
            >
              <option value="">All Levels</option>
              {options.data?.levels.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <div className="d-flex justify-content-between align-items-center gap-2 mt-4">
              <button
                className="btn btn-link p-0 small"
                onClick={() => {
                  setInput("");
                  setParams({});
                }}
              >
                Clear filters
              </button>
              <small className="text-muted" aria-live="polite">
                {query.data
                  ? `${query.data.total} ${query.data.total === 1 ? "result" : "results"}`
                  : "—"}
              </small>
            </div>
            {options.isError && (
              <div className="small mt-3" role="alert">
                Filters could not be loaded.{" "}
                <button
                  className="btn btn-link p-0 small"
                  onClick={() => options.refetch()}
                >
                  Retry filters
                </button>
              </div>
            )}
          </div>
          <div className="course-card course-tip mt-4">
            <h2>
              <Info size={16} /> Programme Detail
            </h2>
            <p>
              Select a course to view institutional requirements, availability,
              and application steps.
            </p>
          </div>
        </aside>
        <main aria-label="Course results" aria-busy={query.isFetching}>
          {!validFilters && (
            <div className="course-card p-4" role="alert">
              Invalid course filters. Clear filters to start again.
            </div>
          )}
          {validFilters && query.isLoading && (
            <div className="course-card p-4" role="status">
              <Loader2 className="me-2" size={24} aria-hidden="true" />
              Loading courses…
            </div>
          )}
          {validFilters && query.isError && (
            <QueryError
              retry={() => {
                query.refetch();
              }}
            />
          )}
          {validFilters && !query.isError && query.data?.items.length === 0 && (
            <div className="course-card p-4">
              <SearchX
                size={28}
                className="text-muted mb-3"
                aria-hidden="true"
              />
              <h2 className="h6">No courses found</h2>
              <p className="text-muted mb-0">
                Try another search or clear your filters.
              </p>
            </div>
          )}
          {validFilters &&
            !query.isError &&
            query.data?.items.map((course) => (
              <article
                className="course-card course-result"
                key={course.offeringId}
              >
                <div className="course-result-content">
                  <h2>{course.programmeName}</h2>
                  <CourseFields course={course} />
                </div>
                <div className="course-result-links">
                  <ApplyAction course={course} />
                  <Link
                    to={`/courses/${course.offeringId}?${params.toString()}`}
                  >
                    View details
                  </Link>
                </div>
              </article>
            ))}
          {validFilters &&
            !query.isError &&
            query.data &&
            query.data.total > 12 && (
              <nav className="course-pagination" aria-label="Course pages">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page <= 1}
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    next.set("page", String(page - 1));
                    setParams(next);
                  }}
                >
                  Previous
                </button>
                <span>
                  Page {page} of {Math.ceil(query.data.total / 12)}
                </span>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page * 12 >= query.data.total}
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    next.set("page", String(page + 1));
                    setParams(next);
                  }}
                >
                  Next
                </button>
              </nav>
            )}
        </main>
      </div>
    </div>
  );
}
export function CourseDetailPage() {
  const { user } = useAuth();
  const { offeringId = "" } = useParams();
  const [params] = useSearchParams();
  const valid =
    /^[1-9]\d*$/.test(offeringId) && Number.isSafeInteger(Number(offeringId));
  const query = useQuery(
    ["course", user?.id, offeringId],
    ({ signal }) => agentApi.getCourse(Number(offeringId), signal),
    { enabled: !!user?.id && valid },
  );
  return (
    <div className="course-search container-fluid py-4">
      <Link
        className="d-inline-flex gap-2 align-items-center mb-4"
        to={`/courses?${params.toString()}`}
      >
        <ArrowLeft size={16} />
        Back to results
      </Link>
      <h1 className="mb-4">Programme details</h1>
      {!valid ? (
        <div role="alert">
          Invalid course identifier. Please select a course again.
        </div>
      ) : query.isLoading ? (
        <div className="course-card p-4" role="status">
          Loading course details…
        </div>
      ) : query.isError ? (
        <div className="course-card p-4" role="alert">
          <h2 className="h6">Course details unavailable</h2>
          <p>
            The course may no longer be listed, or the request could not be
            completed.
          </p>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => query.refetch()}
          >
            Retry
          </button>
        </div>
      ) : (
        query.data && (
          <article className="course-card course-detail">
            <h2>{query.data.programmeName}</h2>
            <p className="text-muted">{query.data.institutionName}</p>
            <CourseFields course={query.data} details />
            <h3>Description</h3>
            <p className="course-description">
              {fallback(query.data.description)}
            </p>
            <h3>Availability and application</h3>
            <p>
              {query.data.availability}. Listing does not guarantee seats or
              admission.
            </p>
            <ApplyAction course={query.data} />
          </article>
        )
      )}
    </div>
  );
}
